import downloadService from '@/features/adp/services/download/download.service';
import { closeOverlay } from '@/features/adp/state/overlaySlice';
import type { DqReportOverlayData } from '@/features/adp/state/overlaySlice';
import { useAppDispatch, useAppSelector } from '@/app/reduxHooks';
import { Button } from '@/ui/button';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@/ui/dialog';
import { useState } from 'react';
import { toast } from 'sonner';
import styles from './DqReportModal.module.scss';
import { MaterialIcon } from '@/ui/material-icon';

const DqReportModal = () => {
  const dispatch = useAppDispatch();
  const { isOpen, type, data } = useAppSelector(state => state.overlay);
  const [isDownloading, setIsDownloading] = useState(false);

  const isVisible = isOpen && type === 'dq-report-modal';
  const modalData: DqReportOverlayData | null =
    isVisible && data && 'pipelineId' in data ? data : null

  const pipelineId = modalData?.pipelineId ?? '';
  const pipelineName = modalData?.pipelineName ?? '';
  const passed = modalData?.passedRecords ?? 0;
  const failed = modalData?.failedRecords ?? 0;

  const handleClose = () => dispatch(closeOverlay())

  const handleDownload = async () => {
    if (!pipelineId) return
    setIsDownloading(true)
    try {
      await downloadService.downloadDQReport(pipelineId)
      handleClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Dialog
      open={isVisible}
      onOpenChange={open => {
        if (!open) handleClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className={styles.modalTitle}>
            Data Quality Report — {pipelineName}
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className={styles.qualityStats}>
            <div className={`${styles.qualityStatCard} ${styles.qualityStatCardPassed}`}>
              <span className={styles.qualityStatValue}>{passed}</span>
              <span className={styles.qualityStatLabel}>Passed</span>
            </div>
            <div className={`${styles.qualityStatCard} ${styles.qualityStatCardFailed}`}>
              <span className={styles.qualityStatValue}>{failed}</span>
              <span className={styles.qualityStatLabel}>Failed</span>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <p className='text-sm text-muted-foreground'>
              All the failed records can be downloaded below.
            </p>

            <MaterialIcon name='download' size={120} />

            <Button
              type="button"
              variant="default"
              size="default"
              onClick={handleDownload}
              disabled={isDownloading}
            >
              {isDownloading ? 'Downloading...' : 'Download Failed Records'}
            </Button>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

export default DqReportModal
